// fallow-ignore-file circular-dependency
// fallow-ignore-file unused-enum-member
import type { RuleSet } from '../components/filters/data-filter/data-filter.component';
import type {
	ChartCardConfig,
	FilterConfig,
	SourceConfig,
	TimeDimensionConfig,
} from './chart-config.model';
import { ChartType } from './chart-config.model';
import type { MapCardConfig } from './map-config.model';
import type { TableCardConfig } from './table-config.model';

/**
 * Image reference data delivered via WebSocket for image cards.
 * All refs are optional because an event may only update a subset.
 */
export interface ImageDataRefs {
	imageRef?: string;
	imageRefLight?: string;
	imageRefDark?: string;
}

/**
 * Error severity levels for card errors
 */
export enum CardErrorSeverity {
	/** Informational - something unexpected but recoverable */
	Info = 'info',
	/** Warning - partial data or degraded functionality */
	Warning = 'warning',
	/** Error - operation failed, card cannot display data */
	Error = 'error',
	/** Critical - system-level failure */
	Critical = 'critical',
}

/**
 * Error codes for categorizing card errors
 */
export enum CardErrorCode {
	/** Unknown error */
	Unknown = 'UNKNOWN',
	/** Network/connection error */
	NetworkError = 'NETWORK_ERROR',
	/** Request timeout */
	Timeout = 'TIMEOUT',
	/** Invalid configuration */
	InvalidConfig = 'INVALID_CONFIG',
	/** Data fetch failed */
	DataFetchFailed = 'DATA_FETCH_FAILED',
	/** Data transformation failed */
	DataTransformFailed = 'DATA_TRANSFORM_FAILED',
	/** Authentication error */
	AuthenticationError = 'AUTHENTICATION_ERROR',
	/** Authorization error */
	AuthorizationError = 'AUTHORIZATION_ERROR',
	/** Rate limit exceeded */
	RateLimitExceeded = 'RATE_LIMIT_EXCEEDED',
	/** Backend service error */
	ServiceError = 'SERVICE_ERROR',
	/** Invalid response from backend */
	InvalidResponse = 'INVALID_RESPONSE',
	/** No data available */
	NoData = 'NO_DATA',
	/** WebSocket connection error */
	WebSocketError = 'WEBSOCKET_ERROR',
}

/**
 * Structured error information for debugging card issues.
 * Provides detailed context for developers while keeping user-facing messages clean.
 */
export interface CardError {
	/** Error code for categorization */
	code: CardErrorCode;

	/** Severity level of the error */
	severity: CardErrorSeverity;

	/** User-friendly error message */
	message: string;

	/** Technical details for debugging (not shown to users) */
	details?: string;

	/** Original error object or message */
	originalError?: unknown;

	/** Timestamp when the error occurred */
	timestamp: string;

	/** Request ID associated with the failed request */
	requestId?: string;

	/** Execution ID if applicable */
	executionId?: string;

	/** Stack trace for debugging (only in development) */
	stack?: string;

	/** Additional context for debugging */
	context?: Record<string, unknown>;

	/** Whether the error is recoverable (can retry) */
	recoverable?: boolean;

	/** Suggested action to resolve the error */
	suggestedAction?: string;
}

export enum CardType {
	Chart = 'chart',
	Table = 'table',
	Map = 'map',
	Markdown = 'markdown',
	Html = 'html',
	Image = 'image',
	Filter = 'filter',
	Analysis = 'analysis',
}

/**
 * Base card configuration
 * Matches API schema: CardItem
 *
 * Note: accountId is NOT part of the card - it's passed as a query parameter
 * Note: sortKey is a DynamoDB internal field, not returned by API
 * Note: `data` property is NOT in base - each card type defines its own data structure
 */
export interface BaseCardConfig {
	/** Identity fields (from API: CardItem) */
	cardId: string /** Unique identifier for the card */;
	pageId?: string /** Page ID of the page containing the card */;
	viewId?: string /** View ID containing the card (used for routing/context) */;
	clientId?: string /** Client ID the card belongs to */;

	/** Card type */
	cardType: CardType /** Card type (chart, markdown, html, filter) */;

	/** Display metadata */
	title?: string /** Card title */;
	subtitle?: string /** Card subtitle */;
	icon?: string /** Material icon name (e.g., "trending_up", "bar_chart") */;
	image?: string /** Card image URL */;

	/** Versioning */
	version?: number /** Card version number */;
	/** Number of server revisions (for optimistic locking during persistence) */
	revisions?: number;

	/** Timestamps */
	dateCreated?: string /** ISO timestamp of card creation */;
	dateUpdated?: string /** ISO timestamp of card last update */;

	/** User tracking */
	createdBy?: string /** Creator user ID */;
	userId?: string /** User ID (alternative to createdBy) */;

	/** Request tracking */
	requestId?: string /** ID for tracking API requests and responses */;

	/** Content (for markdown/HTML cards) */
	content?: string /** Main content for markdown/HTML cards */;

	/** Styling (from API: CardStyle) */
	style?: CardStyle /** Card style configuration */;

	/** Grid position (when card is in a layout) */
	x?: number /** X position in grid */;
	y?: number /** Y position in grid */;
	w?: number /** Width in grid units */;
	h?: number /** Height in grid units */;

	/** Error state for the card */
	error?: CardError;

	/**
	 * Saved filters attached to this card (`getItem.card.filters`).
	 * Distinct from inline `data.filters` query rules on chart/table cards.
	 */
	linkedFilters?: CardLinkedFilter[];

	/** Write-only patch field for `updateCard` (`CardInput.filterIds`). */
	filterIds?: string[];
}

/**
 * Card color configuration
 * From API: CardColor
 */
export interface CardColor {
	background?: string;
	border?: string;
	shadow?: string;
}

/**
 * Card style color for a theme variant
 * From API: CardStyleColor
 */
export interface CardStyleColor {
	card?: CardColor;
}

/**
 * Card style colors for light/dark themes
 * From API: CardStyleColors
 */
export interface CardStyleColors {
	light?: CardStyleColor;
	dark?: CardStyleColor;
}

/**
 * Card style property configuration
 * From API: CardStyleProperty
 */
export interface CardStyleProperty {
	padding?: string;
	contentAlignment?: string;
}

/**
 * Interface for card styling options
 * From API: CardStyle
 */
export interface CardStyle {
	card?: CardStyleProperty;
	colors?: CardStyleColors;
}

/**
 * Markdown card configuration.
 * Content is stored in the 'content' field (inherited from BaseCardConfig).
 */
export interface MarkdownCardConfig extends BaseCardConfig {
	cardType: CardType.Markdown /** Card type identifier */;
	content: string /** Markdown content */;
}

/**
 * HTML card configuration.
 * Content is stored in the 'content' field (inherited from BaseCardConfig).
 */
export interface HtmlCardConfig extends BaseCardConfig {
	cardType: CardType.Html /** Card type identifier */;
	content: string /** HTML content */;
}

/**
 * Image card configuration.
 * Supports a single image (imageRef) or theme-specific images (imageRefLight, imageRefDark).
 */
export interface ImageCardConfig extends BaseCardConfig {
	cardType: CardType.Image /** Card type identifier */;
	/** S3 or URL reference for the image. Fallback when theme-specific refs are not set. */
	imageRef?: string;
	/** S3 or URL reference for the image in light mode. */
	imageRefLight?: string;
	/** S3 or URL reference for the image in dark mode. */
	imageRefDark?: string;
	/** Max image height in px. Omit = no constraint. */
	maxHeight?: number | null;
	/** Max image width in px. Omit or null = auto (no constraint). */
	maxWidth?: number | null;
}

/** Publication status values for an analysis item. */
export type AnalysisStatus = 'DRAFT' | 'PUBLISHED';

/**
 * One analysis on an analysis card (carousel + drawer; config and API share this shape).
 * `startTimestamp` / `endTimestamp` are UTC instants as Unix epoch milliseconds; `endTimestamp` is inclusive.
 */
export interface Analysis {
	/**
	 * Persisted analysis key (GraphQL `analysisId`). Empty until the server assigns one.
	 * Editor drafts may hold a client id until the first successful save.
	 */
	id: string;
	/**
	 * UI/WS correlation key when `id` is not yet persisted (timestamp-derived).
	 * Never sent to GraphQL — use the persisted id before mutations once available.
	 */
	runtimeId?: string;
	startTimestamp: number;
	endTimestamp: number;
	label?: string;
	headline: string;
	content: string;
	/** Publication state for the analysis item. */
	status?: AnalysisStatus;
	/** Optimistic-lock revision for the analysis item (not card `revisions`). */
	revisions?: number;
}

/**
 * Analysis card configuration.
 * Persisted rows live in `analyses` (saved via GraphQL). The grid reads live
 * rows from WebSocket `data.analyses[]` on the corresponding data signal, like markdown `content`.
 */
export interface AnalysisCardConfig extends BaseCardConfig {
	cardType: CardType.Analysis /** Card type identifier */;
	analyses: Analysis[];
}

/**
 * Persisted scope for which cards a filter card affects.
 * Matches AppSync `CardFilterGroup` / `CardFilterGroupInput`.
 */
export interface CardFilterGroup {
	groupId?: string;
	targetCardIds?: string[];
}

/** Saved library filter linked on a chart/table card (`CardItem.filters` from AppSync). */
export interface CardLinkedFilter {
	filterId: string;
	label: string;
	isGlobal?: boolean;
	/** `FilterItem.filter` AWSJSON when returned by `getItem.card`. */
	filter?: unknown;
}

/** Draft from the filter menu Apply action or chip remove. */
export interface CardFilterPersistDraft {
	inlineFilters: FilterConfig[];
	filterIds: string[];
	linkedFilters?: CardLinkedFilter[];
	/**
	 * When true, `createFilter` already linked this filter to the card on the server
	 * (`FilterInput.cardId`) — skip redundant `updateCard` to avoid revision conflicts.
	 */
	skipCardPersist?: boolean;
}

/** Options when flushing card config after applying {@link CardFilterPersistDraft}. */
export interface CardConfigFlushOptions {
	/** When set, flush failures log draft context and show a snackbar before rethrowing. */
	readonly filterDraft?: CardFilterPersistDraft;
}

/** Panel overlay vs full drawer shell for {@link JeptoCardFilterBuilderComponent}. */
export type CardFilterBuilderPresentation = 'panel' | 'drawer';

/** Row in the “Filters on this card” list inside {@link JeptoCardFilterContainerComponent}. */
export interface CardFilterOnCardItem {
	id: string;
	label: string;
	filter: FilterConfig;
	/** All leaf rules when moved from a saved preset (e.g. OR group). */
	filters?: FilterConfig[];
	/** When true, shows the “Saved Filter” badge (Figma w/ Filters). */
	fromSavedFilter?: boolean;
	/** Source `filterId` when moved from Saved Filters. */
	savedFilterId?: string;
	/** Full boolean tree for the edit form (`FilterItem.filter` AWSJSON). */
	ruleSet?: RuleSet;
	/** Set when linked/saved filter AWSJSON could not be parsed for editing. */
	payloadError?: string;
	reportCount?: number;
	cardCount?: number;
	dateUpdated?: string;
	/** Optimistic-lock revision for `updateFilter`. */
	revisions?: number;
}

/** Saved filter preset in the “Saved Filters” section (provided by the host). */
export interface CardFilterSavedFilter {
	id: string;
	name: string;
	filters: FilterConfig[];
	ruleSet?: RuleSet;
	reportCount?: number;
	cardCount?: number;
	dateUpdated?: string;
	revisions?: number;
}

/**
 * Control type definitions
 */
export enum ControlType {
	DateRange = 'date_range',
	Dropdown = 'dropdown',
	Search = 'search',
}

export interface FilterCardDataConfig {
	dimensions?: Array<{ name: string; label?: string }>;
	/** @deprecated Read `title` on the card — legacy editor state only. */
	searchLabel?: string;
	/** @deprecated Read `subtitle` on the card — legacy editor state only. */
	placeholder?: string;
	/** Editor / local state; persisted on save via `data.filters` (AppSync allow-list). */
	selected?: string[];
	/** Default selection when returned from `getCard` after save. */
	filters?: FilterConfig[];
	/** Time context for `getCardData` when loading dropdown options. */
	timeDimension?: TimeDimensionConfig;
}

export interface FilterCardConfig extends BaseCardConfig {
	cardType: CardType.Filter;
	controlType: ControlType;
	sources?: SourceConfig[];
	data?: FilterCardDataConfig;
	filterGroup?: CardFilterGroup;
}

export type CardConfig =
	| ChartCardConfig
	| TableCardConfig
	| MapCardConfig
	| FilterCardConfig
	| MarkdownCardConfig
	| HtmlCardConfig
	| ImageCardConfig
	| AnalysisCardConfig;

export function isFilterCardConfig(
	config: CardConfig | undefined
): config is FilterCardConfig {
	return config?.cardType === CardType.Filter;
}

/**
 * Base card data response from WebSocket
 * This is the data returned by the backend after processing a card query.
 */
export interface CardData {
	cardId: string;
	cardType: CardType;
	dataId?: string;
	executionId: string;
	pageId?: string;
	viewId?: string;
	requestId?: string;

	/** Error state for the card */
	error?: CardError;
}

/**
 * Series data point - tuple of [timestamp/label, value]
 */
export type SeriesDataPoint = [string, number];

/**
 * Individual series data from backend
 */
export interface SeriesData {
	metricId: string;
	name: string;
	label: string;
	data: SeriesDataPoint[];
	aggregationValue?: number;
	comparisonData?: SeriesDataPoint[];
	comparisonAggregationValue?: number;
	comparisonDates?: {
		start: string;
		end: string;
	};
	comparisonPeriod?: string;
}

/**
 * Column definition for table charts
 */
export interface ColumnDef {
	id: string;
	field: string;
	headerName: string;
	type: string;
}

/**
 * Chart data response from WebSocket
 * This is what the backend sends after processing a chart card query.
 */
export interface ChartDataResponse extends CardData {
	cardType: CardType.Chart;
	chartType: ChartType | string;
	data: {
		/** Series data for line/bar/area/column/scatter charts */
		series?: SeriesData[];
		/** Donut series data for donut/pie charts */
		donutSeries?: {
			name: string;
			value: number;
		}[];
		/** Total value for donut charts */
		total?: number;
		/** Center label for donut charts */
		centerLabel?: {
			title: string;
			subtitle?: string;
		};
		/** X-axis label for scatter charts */
		xAxisLabel?: string;
		/** Y-axis label for scatter charts */
		yAxisLabel?: string;
		/** Column definitions for table charts */
		columnDefs?: ColumnDef[];
		/** Row data for table charts */
		rowData?: Record<string, any>[];
		/** Number chart primary KPI value */
		value?: number;
		/** Number chart period comparison (% change) */
		comparisonData?: {
			value: number;
			unit?: string;
		};
		/** Number chart sparkline points */
		sparklineData?: SeriesDataPoint[];
		/** Number chart display format */
		format?: {
			prefix?: string;
			suffix?: string;
			precision?: number;
		};
	};
}

/**
 * Table query payload (WebSocket or `cardData`); stored in the same per-card
 * map as {@link ChartDataResponse} in {@link DataService}.
 */
export interface TableDataResponse extends CardData {
	cardType: CardType.Table;
	chartType?: string;
	data: ChartDataResponse['data'];
}

/** Map value for chart and table cards in the analytics `DataService` data map. */
export type QueryCardDataResponse = ChartDataResponse | TableDataResponse;

/**
 * HTML card data format used by components
 */
interface HtmlCardData extends CardData {
	content: string;
}

/**
 * Markdown card data format used by components
 */
interface MarkdownCardData extends CardData {
	content: string;
}

/**
 * Union type for all card data responses
 */
type CardDataResponse = ChartDataResponse | HtmlCardData | MarkdownCardData;
